import React, { useEffect, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import EventService from "../../services/EventService";
import EventShell, { EventError, EventLoading } from "./EventShell";
import PrivacyPolicy from "../legal/PrivacyPolicy";
import TermsAndConditions from "../legal/TermsAndConditions";
import { formatEventRange, locationLabel, viewerTimeZone } from "./eventFormat";

const STORAGE_KEY = "kinshieldEventRegistrations";

const LEGAL_VIEWS = {
  terms: { title: "Terms & conditions", Doc: TermsAndConditions },
  privacy: { title: "Privacy policy", Doc: PrivacyPolicy },
};

/** Keeps a slug → manage-token map so a returning attendee lands on their ticket. */
export const rememberRegistration = (slug, token) => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    saved[slug] = token;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(saved));
  } catch {
    // Private browsing or a full quota — the confirmation email still has the link.
  }
};

export const recallRegistration = (slug) => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}")[slug] || null;
  } catch {
    return null;
  }
};

/** Registration form at /events/:slug/register. */
const EventRegister = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    organization: "",
    partySize: 1,
    ticketTypeId: "",
  });
  const [answers, setAnswers] = useState({});
  const [smsConsent, setSmsConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  /** Legal docs swap in over the form so a half-filled form is never lost. */
  const [legalView, setLegalView] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      const result = await EventService.getEvent(slug);
      if (!active) return;
      if (result.success) {
        setEvent(result.event);
        const types = result.event.ticketTypes || [];
        if (types.length === 1) {
          setForm((prev) => ({ ...prev, ticketTypeId: types[0].id }));
        }
      } else {
        setError(result.message || "We couldn't find that event.");
      }
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [legalView]);

  const setField = (name) => (e) =>
    setForm((prev) => ({ ...prev, [name]: e.target.value }));

  const handleCancel = () => {
    const touched =
      Object.values(form).some(
        (value) => String(value).trim() !== "" && String(value) !== "1",
      ) || Object.values(answers).some((value) => String(value).trim() !== "");
    if (
      touched &&
      !window.confirm("Discard what you've entered and go back to the event?")
    ) {
      return;
    }
    navigate(`/events/${slug}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.email.trim()) {
      setError("Please enter an email address so we can send your ticket.");
      return;
    }

    const missing = (event.questions || [])
      .filter((q) => q.required && !String(answers[q.key] || "").trim())
      .map((q) => q.label);
    if (missing.length > 0) {
      setError(`Please answer: ${missing.join(", ")}.`);
      return;
    }

    setSubmitting(true);
    const result = await EventService.register(slug, {
      ...form,
      partySize: Number(form.partySize) || 1,
      ticketTypeId: form.ticketTypeId ? Number(form.ticketTypeId) : null,
      answers,
      smsConsent,
      marketingConsent,
      attendeeTimezone: viewerTimeZone(),
      source:
        searchParams.get("utm_source") || searchParams.get("source") || null,
    });
    setSubmitting(false);

    if (!result.success) {
      setError(result.message || "Registration failed. Please try again.");
      return;
    }

    rememberRegistration(slug, result.registration.manageToken);
    navigate(
      `/events/${slug}/confirmed?t=${encodeURIComponent(result.registration.manageToken)}`,
    );
  };

  if (loading) {
    return (
      <EventShell narrow>
        <EventLoading label="Loading…" />
      </EventShell>
    );
  }

  if (!event) {
    return (
      <EventShell narrow>
        <EventError message={error || "Event not found."} />
        <Link className="ev-btn ev-btn-secondary" to="/events">
          Back to all events
        </Link>
      </EventShell>
    );
  }

  const waitlisting = event.full && event.waitlistEnabled;
  const closed =
    !event.registrationOpen ||
    event.status === "CANCELLED" ||
    (event.full && !event.waitlistEnabled);

  const crumbs = [
    { label: "Events", to: "/events" },
    { label: event.title, to: `/events/${slug}` },
    { label: waitlisting ? "Join the waitlist" : "Register" },
  ];

  if (legalView) {
    const { title, Doc } = LEGAL_VIEWS[legalView];
    return (
      <EventShell crumbs={[crumbs[0], crumbs[1], { label: title }]}>
        <button
          type="button"
          className="ev-btn ev-btn-secondary"
          onClick={() => setLegalView(null)}
        >
          <FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" />
          Back to registration
        </button>
        <div className="ev-legal">
          <Doc />
        </div>
        <button
          type="button"
          className="ev-btn ev-btn-secondary"
          onClick={() => setLegalView(null)}
        >
          <FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" />
          Back to registration
        </button>
      </EventShell>
    );
  }

  return (
    <EventShell narrow crumbs={crumbs}>
      <span className="ev-eyebrow">Step 1 of 1 — free</span>
      <h1 className="ev-h1">
        {waitlisting ? "Join the waitlist" : "Register for this session"}
      </h1>

      <div className="ev-panel ev-panel-accent">
        <p className="ev-panel-head">{event.title}</p>
        <p className="ev-muted" style={{ margin: 0 }}>
          {formatEventRange(event.startsAt, event.endsAt)}
        </p>
        <p className="ev-muted" style={{ margin: "4px 0 0" }}>
          {locationLabel(event)}
        </p>
        {event.venue?.address && (
          <p className="ev-hint" style={{ margin: 0 }}>
            {event.venue.address}
          </p>
        )}
      </div>

      {closed && (
        <div className="ev-alert ev-alert-error" role="alert">
          Registration for this event is closed.
        </div>
      )}

      {waitlisting && !closed && (
        <div className="ev-alert ev-alert-info">
          This event is currently full. Join the waitlist and we'll email you
          right away if a seat opens up.
        </div>
      )}

      {error && <EventError message={error} />}

      <form onSubmit={handleSubmit} noValidate>
        <div className="ev-panel">
          <p className="ev-panel-head">Who's coming</p>
          <p className="ev-hint">
            Only your email address is required. Everything else helps us
            prepare.
          </p>

          <div className="ev-field-row">
            <div className="ev-field">
              <label htmlFor="ev-first">First name</label>
              <input
                id="ev-first"
                type="text"
                value={form.firstName}
                onChange={setField("firstName")}
                autoComplete="given-name"
              />
            </div>
            <div className="ev-field">
              <label htmlFor="ev-last">Last name</label>
              <input
                id="ev-last"
                type="text"
                value={form.lastName}
                onChange={setField("lastName")}
                autoComplete="family-name"
              />
            </div>
          </div>

          <div className="ev-field">
            <label htmlFor="ev-email">
              Email address <span className="ev-required">*</span>
            </label>
            <input
              id="ev-email"
              type="email"
              required
              value={form.email}
              onChange={setField("email")}
              autoComplete="email"
              aria-describedby="ev-email-hint"
            />
            <div className="ev-hint" id="ev-email-hint">
              We'll send your ticket and a reminder here. We never sell your
              address.
            </div>
          </div>

          <div className="ev-field">
            <label htmlFor="ev-phone">Phone number (optional)</label>
            <input
              id="ev-phone"
              type="tel"
              value={form.phone}
              onChange={setField("phone")}
              autoComplete="tel"
            />
          </div>

          <div className="ev-field">
            <label htmlFor="ev-org">Organization (optional)</label>
            <input
              id="ev-org"
              type="text"
              value={form.organization}
              onChange={setField("organization")}
              autoComplete="organization"
            />
          </div>

          <div className="ev-field">
            <label htmlFor="ev-party">How many people are coming?</label>
            <input
              id="ev-party"
              type="number"
              min="1"
              max="20"
              value={form.partySize}
              onChange={setField("partySize")}
              aria-describedby="ev-party-hint"
            />
            <div className="ev-hint" id="ev-party-hint">
              Include yourself. Bring a friend or family member — most people
              do.
            </div>
          </div>

          {(event.ticketTypes || []).length > 1 && (
            <div className="ev-field">
              <label htmlFor="ev-ticket">Ticket type</label>
              <select
                id="ev-ticket"
                value={form.ticketTypeId}
                onChange={setField("ticketTypeId")}
              >
                <option value="">Select one</option>
                {event.ticketTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {(event.questions || []).map((question) => (
            <div className="ev-field" key={question.key}>
              <label htmlFor={`ev-q-${question.key}`}>
                {question.label}
                {question.required && <span className="ev-required"> *</span>}
              </label>
              {question.type === "select" ? (
                <select
                  id={`ev-q-${question.key}`}
                  value={answers[question.key] || ""}
                  onChange={(e) =>
                    setAnswers((prev) => ({
                      ...prev,
                      [question.key]: e.target.value,
                    }))
                  }
                >
                  <option value="">Select one</option>
                  {(question.options || []).map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              ) : question.type === "textarea" ? (
                <textarea
                  id={`ev-q-${question.key}`}
                  value={answers[question.key] || ""}
                  onChange={(e) =>
                    setAnswers((prev) => ({
                      ...prev,
                      [question.key]: e.target.value,
                    }))
                  }
                />
              ) : (
                <input
                  id={`ev-q-${question.key}`}
                  type="text"
                  value={answers[question.key] || ""}
                  onChange={(e) =>
                    setAnswers((prev) => ({
                      ...prev,
                      [question.key]: e.target.value,
                    }))
                  }
                />
              )}
            </div>
          ))}
        </div>

        <div className="ev-panel">
          <p className="ev-panel-head">Staying in touch</p>

          {/* Consents are separate, unchecked, and individually labelled —
              bundling a TCPA text opt-in into a single "I agree" is not
              consent. */}
          <label className="ev-consent">
            <input type="checkbox" checked disabled readOnly />
            <span className="ev-consent-text">
              Email me my ticket and a reminder about this event.
              <span className="ev-hint">
                Required — this is how we send your confirmation.
              </span>
            </span>
          </label>

          <label className="ev-consent">
            <input
              type="checkbox"
              checked={smsConsent}
              onChange={(e) => setSmsConsent(e.target.checked)}
            />
            <span className="ev-consent-text">
              Text me a reminder an hour before the event.
              <span className="ev-hint">
                Message and data rates may apply. Reply STOP to opt out.{" "}
                {/* Its own opt-in form, so this one stays a separate page. */}
                <a
                  href="/sms-consent"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  SMS terms
                </a>
              </span>
            </span>
          </label>

          <label className="ev-consent">
            <input
              type="checkbox"
              checked={marketingConsent}
              onChange={(e) => setMarketingConsent(e.target.checked)}
            />
            <span className="ev-consent-text">
              Send me scam alerts and KinShield updates.
              <span className="ev-hint">Occasional. Unsubscribe any time.</span>
            </span>
          </label>
        </div>

        <div className="ev-form-actions">
          <button
            type="submit"
            className="ev-btn ev-btn-primary"
            disabled={submitting || closed}
          >
            {submitting
              ? "Submitting…"
              : waitlisting
                ? "Join the waitlist"
                : "Complete registration"}
          </button>
          <button
            type="button"
            className="ev-btn ev-btn-quiet"
            onClick={handleCancel}
            disabled={submitting}
          >
            Cancel
          </button>
        </div>

        <p className="ev-hint">
          By registering you agree to our{" "}
          <button
            type="button"
            className="ev-inline-link"
            onClick={() => setLegalView("terms")}
          >
            terms
          </button>{" "}
          and{" "}
          <button
            type="button"
            className="ev-inline-link"
            onClick={() => setLegalView("privacy")}
          >
            privacy policy
          </button>
          .
        </p>
      </form>
    </EventShell>
  );
};

export default EventRegister;
